import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { downloadDietPDF, printDiet } from '../../utils/dietPdf';
import { translateDietToTamil } from '../../utils/tamilTranslate';
import Loader from '../layouts/Loader';
import formatDate from '../../utils/formatDate';

export default function PublicDiet() {
    const { tokenOrId } = useParams();
    const [searchParams] = useSearchParams();
    const [diet, setDiet] = useState(null);
    const [tamilDiet, setTamilDiet] = useState(null);
    const [language, setLanguage] = useState(searchParams.get('lang') === 'ta' ? 'ta' : 'en');
    const [translating, setTranslating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchDiet = async () => {
            try {
                setLoading(true);
                const { data } = await axios.get(`/api/v1/public/diet/${tokenOrId}`);
                if (isMounted) {
                    setDiet(data.diet);
                    setError(null);

                    // If URL specified lang=ta, translate immediately
                    if (searchParams.get('lang') === 'ta' && data.diet) {
                        setTranslating(true);
                        try {
                            const translated = await translateDietToTamil(data.diet);
                            if (isMounted) setTamilDiet(translated);
                        } finally {
                            if (isMounted) setTranslating(false);
                        }
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setError(err.response?.data?.message || 'Invalid or expired diet link');
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        if (tokenOrId) fetchDiet();

        return () => {
            isMounted = false;
        };
    }, [tokenOrId, searchParams]);

    const handleLanguageChange = async (newLang) => {
        setLanguage(newLang);
        if (newLang === 'ta' && !tamilDiet && diet) {
            setTranslating(true);
            try {
                const translated = await translateDietToTamil(diet);
                setTamilDiet(translated);
            } catch (err) {
                console.error(err);
            } finally {
                setTranslating(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <Loader />
            </div>
        );
    }

    if (error || !diet) {
        return (
            <div className="container py-5 text-center">
                <div className="card p-4 mx-auto shadow-sm" style={{ maxWidth: '480px', borderRadius: '12px' }}>
                    <div className="text-danger mb-3" style={{ fontSize: '3rem' }}>
                        <i className="fa fa-exclamation-circle"></i>
                    </div>
                    <h4>Diet Plan Not Found</h4>
                    <p className="text-muted">{error || 'This diet link is invalid or may have been removed.'}</p>
                </div>
            </div>
        );
    }

    const currentDiet = (language === 'ta' && tamilDiet) ? tamilDiet : diet;
    const patient = currentDiet.patient || {};
    const slots = Array.isArray(currentDiet.slots) ? currentDiet.slots : [];
    const isTamil = language === 'ta';

    return (
        <div className="container py-4" style={{ maxWidth: '780px' }}>
            <div className="card shadow-sm border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                {/* Header Banner */}
                <div className="p-4 text-white" style={{ backgroundColor: '#1b4d3e' }}>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                        <div>
                            <span className="badge bg-success bg-opacity-75 mb-2">Green Naturopathy</span>
                            <h3 className="fw-bold mb-1">
                                {isTamil ? (currentDiet.title || 'உணவு முறை அட்டவணை') : (currentDiet.title || 'Personalized Diet Chart')}
                            </h3>
                            <p className="small mb-0 text-white-50">
                                {isTamil ? 'இயற்கை மருத்துவ ஊட்டச்சத்து & வாழ்க்கை முறை' : 'Holistic Nutrition & Lifestyle Plan'}
                            </p>
                        </div>
                        <div className="text-end">
                            {/* Language Switcher */}
                            <div className="btn-group btn-group-sm mb-2 shadow-sm">
                                <button
                                    type="button"
                                    className={`btn ${!isTamil ? 'btn-light text-success fw-bold' : 'btn-outline-light'}`}
                                    onClick={() => handleLanguageChange('en')}
                                >
                                    English
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${isTamil ? 'btn-light text-success fw-bold' : 'btn-outline-light'}`}
                                    onClick={() => handleLanguageChange('ta')}
                                >
                                    தமிழ்
                                </button>
                            </div>
                            <div>
                                <span className="badge bg-light text-dark fw-bold">
                                    {formatDate(currentDiet.date)}
                                </span>
                            </div>
                        </div>
                    </div>
                    {translating && (
                        <div className="small text-warning mt-1">
                            <i className="fa fa-spinner fa-spin me-1"></i> Translating diet into Tamil...
                        </div>
                    )}
                </div>

                <div className="card-body p-4">
                    {/* Patient Card (STRICTLY IN ENGLISH AS REQUESTED) */}
                    <div className="bg-light p-3 rounded mb-4 border d-flex justify-content-between align-items-center">
                        <div>
                            <div className="text-muted small">Prescribed For</div>
                            <div className="fw-bold fs-5 text-dark">{patient.name || 'Patient'}</div>
                            <div className="text-muted small">
                                {patient.patientId ? `ID: ${patient.patientId}` : ''}
                                {patient.age ? ` | Age: ${patient.age} Yrs` : ''}
                                {patient.gender ? ` | ${patient.gender}` : ''}
                            </div>
                        </div>
                        <div className="text-end">
                            <div className="text-muted small">Consulting Doctor</div>
                            <div className="fw-bold text-success">
                                {currentDiet.doctor ? (currentDiet.doctor.startsWith('Dr') ? currentDiet.doctor : `Dr. ${currentDiet.doctor}`) : 'Green Naturopathy Team'}
                                {currentDiet.doctorDesignation ? ` (${currentDiet.doctorDesignation})` : ', BNYS'}
                            </div>
                            <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                Authorized Naturopathy Physician
                            </small>
                            {currentDiet.condition && (
                                <div className="text-muted small mt-1">
                                    {currentDiet.condition}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Digital Plan Slots */}
                    {currentDiet.dietType === 'digital' && slots.length > 0 && (
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="fw-bold text-dark mb-0">
                                    <i className="fa fa-utensils text-success me-2"></i>
                                    {isTamil ? 'உணவு அட்டவணை & நேரம்' : 'Daily Meal Schedule'}
                                </h5>
                                <span className="badge bg-light text-muted border">
                                    {isTamil ? `${slots.length} உணவு வேளைகள்` : `${slots.length} Slots`}
                                </span>
                            </div>
                            <div className="list-group">
                                {slots.map((slot, idx) => (
                                    <div key={slot.id || idx} className="list-group-item p-3 mb-2 border rounded shadow-xs">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className="badge bg-success text-white py-1 px-2" style={{ fontSize: '0.75rem' }}>
                                                {slot.time || (isTamil ? 'நேரம்' : 'Schedule')}
                                            </span>
                                            <strong className="text-success">{slot.title}</strong>
                                        </div>
                                        <p className="mb-1 text-dark fw-semibold" style={{ whiteSpace: 'pre-line' }}>
                                            {slot.items}
                                        </p>
                                        {slot.instructions && (
                                            <small className="text-muted fst-italic">
                                                <i className="fa fa-info-circle me-1"></i> {slot.instructions}
                                            </small>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Uploaded File View */}
                    {currentDiet.dietType === 'file' && currentDiet.fileUrl && (
                        <div className="p-4 bg-light rounded text-center border mb-4">
                            <i className={`fa ${currentDiet.fileType === 'pdf' ? 'fa-file-pdf text-danger' : 'fa-image text-primary'} mb-2`} style={{ fontSize: '3rem' }}></i>
                            <h5 className="mb-2">Diet Document Attached</h5>
                            <p className="text-muted small mb-3">Click below to open and review your diet document.</p>
                            <a 
                                href={currentDiet.fileUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="btn btn-outline-success btn-sm px-4"
                            >
                                <i className="fa fa-external-link-alt me-1"></i> View Document
                            </a>
                        </div>
                    )}

                    {/* Guidelines Box */}
                    {currentDiet.guidelines && (
                        <div className="p-3 rounded mb-4" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                            <h6 className="fw-bold text-warning-emphasis mb-2">
                                <i className="fa fa-check-circle me-1"></i>
                                {isTamil ? 'பொதுவான இயற்கை மருத்துவ வழிகாட்டுதல்கள்' : 'Lifestyle Guidelines & Tips'}
                            </h6>
                            <p className="small mb-0 text-dark" style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>
                                {currentDiet.guidelines}
                            </p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="d-flex flex-column flex-sm-row gap-2 mt-4">
                        {currentDiet.dietType === 'digital' ? (
                            <>
                                <button
                                    className="btn btn-success flex-fill py-2 fw-semibold"
                                    style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                                    onClick={() => downloadDietPDF(currentDiet, patient, isTamil ? 'ta' : 'en')}
                                >
                                    <i className="fa fa-download me-2"></i>
                                    {isTamil ? 'தமிழ் PDF பதிவிறக்குக (Download Tamil PDF)' : 'Download Official PDF (English)'}
                                </button>
                                <button
                                    className="btn btn-outline-success py-2 fw-semibold"
                                    onClick={() => downloadDietPDF(currentDiet, patient, isTamil ? 'en' : 'ta')}
                                >
                                    <i className="fa fa-language me-1"></i>
                                    {isTamil ? 'English PDF' : 'தமிழ் PDF'}
                                </button>
                                <button
                                    className="btn btn-outline-dark py-2"
                                    onClick={() => printDiet(currentDiet, patient, isTamil ? 'ta' : 'en')}
                                    title="Print or Save as PDF"
                                >
                                    <i className="fa fa-print"></i>
                                </button>
                            </>
                        ) : (
                            <a
                                href={currentDiet.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-success btn-lg w-100"
                                style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                            >
                                <i className="fa fa-download me-2"></i> Download Diet File
                            </a>
                        )}
                    </div>
                </div>

                <div className="card-footer bg-white text-center py-3 border-top">
                    <small className="text-muted fst-italic">
                        {isTamil ? '"உணவே மருந்து, மருந்தே உணவு" — கிரீன் நேச்சுரோபதி' : '"Let food be thy medicine and medicine be thy food." — Green Naturopathy'}
                    </small>
                </div>
            </div>
        </div>
    );
}
